import React, { useState, useMemo } from 'react';
import {
  SwipeableDrawer,
  Box,
  Typography,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  Button,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export interface MobileIngredientDrawerProps {
  open: boolean;
  onClose: () => void;
  ingredients: string;
  currentScale?: number;
}

const MobileIngredientDrawer: React.FC<MobileIngredientDrawerProps> = ({
  open,
  onClose,
  ingredients,
  currentScale = 1,
}) => {
  const theme = useTheme();
  const [checkedIngredients, setCheckedIngredients] = useState<Set<number>>(new Set());

  // Parse ingredients
  const ingredientList = useMemo(() => {
    return ingredients
      .split('\n')
      .map((ing) => ing.trim())
      .filter((ing) => ing);
  }, [ingredients]);

  const handleToggle = (index: number) => {
    setCheckedIngredients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleClearAll = () => {
    setCheckedIngredients(new Set());
  };

  const usedCount = checkedIngredients.size;
  const totalCount = ingredientList.length;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '70vh',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(46, 33, 23, 0.98) 0%, rgba(32, 22, 16, 0.98) 100%)'
            : '#FFFEF8',
          backgroundImage: theme.palette.mode === 'dark'
            ? 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 248, 240, 0.02) 2px, rgba(255, 248, 240, 0.02) 4px)'
            : 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(139, 69, 19, 0.02) 2px, rgba(139, 69, 19, 0.02) 4px)',
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          pt: 1,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 248, 240, 0.3)'
              : 'rgba(139, 69, 19, 0.3)',
          }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              color: theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.9)'
                : 'rgba(139, 69, 19, 0.9)',
            }}
          >
            Ingredients
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"Crimson Text", serif',
              color: theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.6)'
                : 'rgba(139, 69, 19, 0.6)',
            }}
          >
            {totalCount} ingredients
            {currentScale !== 1 && ` (scaled ×${currentScale})`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Ingredient List */}
      <List sx={{ px: 2, pb: 2, overflowY: 'auto', flex: 1 }}>
        {ingredientList.map((ingredient, index) => (
          <ListItem key={index} disablePadding>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedIngredients.has(index)}
                  onChange={() => handleToggle(index)}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#FF8A65' : '#FF6B35',
                    '&.Mui-checked': {
                      color: theme.palette.mode === 'dark' ? '#6B9B7B' : '#4A7C59',
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: '"Crimson Text", serif',
                    fontSize: '1rem',
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(255, 248, 240, 0.8)'
                      : 'rgba(139, 69, 19, 0.8)',
                    textDecoration: checkedIngredients.has(index) ? 'line-through' : 'none',
                    opacity: checkedIngredients.has(index) ? 0.6 : 1,
                  }}
                >
                  {ingredient}
                </Typography>
              }
              sx={{ width: '100%', ml: 0 }}
            />
          </ListItem>
        ))}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: `1px solid ${theme.palette.mode === 'dark'
            ? 'rgba(255, 248, 240, 0.1)'
            : 'rgba(139, 69, 19, 0.1)'}`,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: '"Crimson Text", serif',
            color: theme.palette.mode === 'dark'
              ? 'rgba(255, 248, 240, 0.7)'
              : 'rgba(139, 69, 19, 0.7)',
          }}
        >
          {usedCount} of {totalCount} used
        </Typography>
        <Button
          size="small"
          onClick={handleClearAll}
          disabled={usedCount === 0}
          sx={{
            fontFamily: '"Crimson Text", serif',
            textTransform: 'none',
          }}
        >
          Clear All
        </Button>
      </Box>
    </SwipeableDrawer>
  );
};

export default MobileIngredientDrawer;
